import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { useAppSettings } from "../../../context/AppSettingsContext";
import {
  Grid,
  Card,
  Box,
  Typography,
  FormControl,
  TextField,
  Button,
  Select,
  MenuItem,
  Table,
  TableContainer,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Checkbox,
  Paper,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormLabel,
  Snackbar,
  Alert,
} from "@mui/material";
import { API_ENDPOINTS } from "../../../config/api";

const EditCustomerOrderForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { deliveryConditions , deliveryDate} = useAppSettings();
  const [appUsers, setAppUsers] = useState([]);
  const [productTypes, setProductTypes] = useState([]);
  const [paymentTerms, setPaymentTerms] = useState([]);
  const [deliveryTerms, setDeliveryTerms] = useState([]);
  const [brands, setBrands] = useState([]);
  const [formData, setFormData] = useState({
    app_user_id: "",
    payment_terms: "",
    payment_terms_description: "",
    delivery_terms: "",
    delivery_address: "",
    delivery_conditions: "",
    delivery_date: "",
    delivery_date_hours: "",
    delivery_date_days: "",
    status: "",
  });
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "error",
  });
  const [loading, setLoading] = useState(true);

  const deliveryConditionsOptions = React.useMemo(() => {
    if (!deliveryConditions) return [];
    
    return deliveryConditions
      .split(",")
      .map((condition, index) => ({
        id: `${index + 1}`,
        name: condition.trim()
      }))
      .filter(condition => condition.name);
  }, [deliveryConditions]);

  
  const deliveryDateOptions = React.useMemo(() => {
    if (!deliveryDate) return [];
    
    return deliveryDate
      .split(",")
      .map((condition, index) => ({
        id: `${index + 1}`,
        name: condition.trim()
      }))
      .filter(condition => condition.name);
  }, [deliveryDate]);


  const staticDeliveryDateOptions = [
    { id: "1", name: "Immediate" },
    { id: "2", name: "Within" },
  ];

  useEffect(() => {
    const fetchData = async () => {
      try {
        const orderResponse = await axios.get(API_ENDPOINTS.CUSTOMER_ORDERS.EDIT(id));
        const orderData = orderResponse.data.customerOrder;
        console.log("Fetched Order Data:", orderData);
        setFormData({
          app_user_id: orderData.app_user_id || "",
          payment_terms: orderData.payment_terms || "",
          payment_terms_description: orderData.payment_terms_description || "",
          delivery_terms: orderData.delivery_terms || "",
          delivery_address: orderData.delivery_address || "",
          delivery_conditions: orderData.delivery_conditions || "",
          delivery_date: orderData.delivery_date || "",
          delivery_date_hours: orderData.delivery_date_hours || "",
          delivery_date_days: orderData.delivery_date_days || "",
          status: orderData.status || "pending",
        });

        const productTypesResponse = await axios.get(API_ENDPOINTS.PRODUCT_TYPE_NAMES);
        const allProductTypes = Array.isArray(productTypesResponse.data)
          ? productTypesResponse.data
          : productTypesResponse.data.data || [];
        setProductTypes(allProductTypes);

        const fetchedProducts = orderResponse.data.products.map((product) => ({
          product_type_id: product.product_type_id,
          brand_id: product.brand_id || "",
          quantity: product.quantity || "",
          pieces: product.pieces || "",
        }));
        setSelectedProducts(fetchedProducts);

        const appUsersResponse = await axios.get(API_ENDPOINTS.APP_USER_NAMES);
        setAppUsers(
          Array.isArray(appUsersResponse.data)
            ? appUsersResponse.data
            : appUsersResponse.data.data || []
        );

        const brandsResponse = await axios.get(API_ENDPOINTS.BRAND_NAMES);
        setBrands(
          Array.isArray(brandsResponse.data)
            ? brandsResponse.data
            : brandsResponse.data.data || []
        );

        const paymentTermsResponse = await axios.get(API_ENDPOINTS.PAYMENT_TERMS.BASE);
        setPaymentTerms(
          Array.isArray(paymentTermsResponse.data.paymentTerms)
            ? paymentTermsResponse.data.paymentTerms
            : Array.isArray(paymentTermsResponse.data)
            ? paymentTermsResponse.data
            : paymentTermsResponse.data.data || []
        );

        const deliveryTermsResponse = await axios.get(API_ENDPOINTS.DELIVERY_TERMS.BASE);
        setDeliveryTerms(
          Array.isArray(deliveryTermsResponse.data.deliveryTerms)
            ? deliveryTermsResponse.data.deliveryTerms
            : Array.isArray(deliveryTermsResponse.data)
            ? deliveryTermsResponse.data
            : deliveryTermsResponse.data.data || []
        );

        setLoading(false);
      } catch (error) {
        console.error("Error fetching data:", error);
        setSnackbar({
          open: true,
          message: "Failed to load Enquiry data",
          severity: "error",
        });
        setLoading(false);
      }
    };

    fetchData();
  }, [id]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleProductSelection = (productTypeId, checked) => {
    if (checked) {
      setSelectedProducts((prev) => [
        ...prev,
        {
          product_type_id: productTypeId,
          brand_id: "",
          quantity: "",
          pieces: "",
        },
      ]);
    } else {
      setSelectedProducts((prev) =>
        prev.filter((p) => p.product_type_id !== productTypeId)
      );
    }
  };

  const handleProductFieldChange = (productTypeId, field, value) => {
    setSelectedProducts((prev) =>
      prev.map((p) =>
        p.product_type_id === productTypeId ? { ...p, [field]: value } : p
      )
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate delivery terms
    if (!formData.delivery_terms) {
      setSnackbar({
        open: true,
        message: "Delivery terms cannot be empty",
        severity: "error",
      });
      return;
    }

    // Validate selected products
    if (selectedProducts.length === 0) {
      setSnackbar({
        open: true,
        message: "At least one product must be selected",
        severity: "error",
      });
      return;
    }

    // Validate each selected product has required fields
    const invalidProducts = selectedProducts.filter(
      product => !product.brand_id || (!product.quantity && !product.pieces)
    );

    if (invalidProducts.length > 0) {
      const productNames = invalidProducts.map(product => {
        const productType = productTypes.find(pt => pt.id === product.product_type_id);
        return productType ? productType.name : 'Unknown product';
      });
      
      setSnackbar({
        open: true,
        message: `Please fill in all required fields (brand and either quantity or pieces) for: ${productNames.join(', ')}`,
        severity: "error",
      });
      return;
    }

    try {
      const payload = {
        ...formData,
        products: selectedProducts,
      };
      console.log("Submitting Payload:", JSON.stringify(payload, null, 2));
      await axios.put(API_ENDPOINTS.CUSTOMER_ORDERS.EDIT(id), payload);
      setSnackbar({
        open: true,
        message: "Enquiry updated successfully!",
        severity: "success",
      });
      setTimeout(() => navigate(-1), 2000);
    } catch (error) {
      console.error("Error updating Enquiry:", error);
      setSnackbar({
        open: true,
        message:
          error.response?.data?.message || "Failed to update Enquiry",
        severity: "error",
      });
    }
  };

  const handleSnackbarClose = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 5 }}>
        <Typography>Loading...</Typography>
      </Box>
    );
  }

  return (
    <Box component="form" onSubmit={handleSubmit}>
      <Grid container spacing={2}>
        <Grid item xs={12} md={8}>
          <Card sx={{ p: 3 }}>
            {/* <Typography variant="h5" sx={{ mb: 2 }}>
              Edit Enquiry
            </Typography> */}
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle1">
                Customer: {appUsers.find((user) => user.id === formData.app_user_id)?.company_name || "Unknown"}
              </Typography>
            </Box>
            <Box mt={4}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Edit Products
              </Typography>
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Select</TableCell>
                      <TableCell>Product Type</TableCell>
                      <TableCell>Brand</TableCell>
                      <TableCell>Quantity</TableCell>
                      <TableCell>Pieces</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {productTypes.map((productType) => {
                      const isSelected = selectedProducts.some(
                        (selected) => selected.product_type_id === productType.id
                      );
                      const selectedProduct = selectedProducts.find(
                        (selected) => selected.product_type_id === productType.id
                      );
                      return (
                        <TableRow key={productType.id}>
                          <TableCell>
                            <Checkbox
                              checked={isSelected}
                              onChange={(e) => handleProductSelection(productType.id, e.target.checked)}
                            />
                          </TableCell>
                          <TableCell>{productType.name}</TableCell>
                          <TableCell>
                            <FormControl fullWidth>
                              <Select
                                value={isSelected ? selectedProduct?.brand_id || "" : ""}
                                onChange={(e) => handleProductFieldChange(
                                  productType.id,
                                  "brand_id",
                                  e.target.value
                                )}
                                disabled={!isSelected}
                              >
                                <MenuItem value="" disabled>
                                  Select Brand
                                </MenuItem>
                                {brands.map((brand) => (
                                  <MenuItem key={brand.id} value={brand.id}>
                                    {brand.name}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </TableCell>
                          <TableCell>
                            <TextField
                              type="number"
                              value={isSelected ? selectedProduct?.quantity || "" : ""}
                              onChange={(e) => handleProductFieldChange(
                                productType.id,
                                "quantity",
                                e.target.value
                              )}
                              disabled={!isSelected}
                              inputProps={{ min: 1 }}
                            />
                          </TableCell>
                          <TableCell>
                            <TextField
                              type="number"
                              value={isSelected ? selectedProduct?.pieces || "" : ""}
                              onChange={(e) => handleProductFieldChange(
                                productType.id,
                                "pieces",
                                e.target.value
                              )}
                              disabled={!isSelected}
                              inputProps={{ min: 0 }}
                            />
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
            <Box mt={2}>
              <Button type="submit" variant="contained">
                  Update Enquiry
              </Button>
              <Button
                variant="outlined"
                color="secondary"
                onClick={() => navigate(-1)}
                sx={{ ml: 2 }}
              >
                Cancel
              </Button>
            </Box>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card sx={{ p: 3, mb: 2 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Payment Terms
            </Typography>
            <FormControl component="fieldset" required>
              <FormLabel component="legend">Select Payment Term</FormLabel>
              <RadioGroup
                name="payment_terms"
                value={formData.payment_terms}
                onChange={handleChange}
              >
                {paymentTerms.map((term) => (
                  <Box key={term.id}>
                    <FormControlLabel
                      value={term.name}
                      control={<Radio />}
                      label={term.name}
                    />
                    {term.name.toLowerCase() === "credit" &&
                      formData.payment_terms === term.name && (
                        <TextField
                          label="Number of Days"
                          name="payment_terms_description"
                          type="number"
                          value={formData.payment_terms_description}
                          onChange={handleChange}
                          inputProps={{ min: 1 }}
                          fullWidth
                          sx={{ mt: 1, ml: 4 }}
                          required
                        />
                      )}
                  </Box>
                ))}
              </RadioGroup>
            </FormControl>
          </Card>
          <Card sx={{ p: 3, mb: 2 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Delivery Terms
            </Typography>
            <FormControl component="fieldset" required>
              <FormLabel component="legend">Select Delivery Term</FormLabel>
              <RadioGroup
                name="delivery_terms"
                value={formData.delivery_terms}
                onChange={handleChange}
              >
                {deliveryTerms.map((term) => (
                  <Box key={term.id}>
                    <FormControlLabel
                      value={term.name}
                      control={<Radio />}
                      label={term.name}
                    />
                    {term.name === "Delivered To" &&
                      formData.delivery_terms === term.name && (
                        <TextField
                          label="Delivery Address"
                          name="delivery_address"
                          multiline
                          rows={3}
                          value={formData.delivery_address}
                          onChange={handleChange}
                          fullWidth
                          sx={{ mt: 1, ml: 4 }}
                          required
                        />
                      )}
                  </Box>
                ))}
              </RadioGroup>
            </FormControl>
          </Card>
          <Card sx={{ p: 3, mb: 2 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Delivery Conditions
            </Typography>
            <FormControl component="fieldset" required>
              <FormLabel component="legend">Select Delivery Condition</FormLabel>
              <RadioGroup
                name="delivery_conditions"
                value={formData.delivery_conditions}
                onChange={handleChange}
              >
                {deliveryConditionsOptions.map((condition) => (
                  <FormControlLabel
                    key={condition.id}
                    value={condition.name}
                    control={<Radio />}
                    label={condition.name}
                  />
                ))}
              </RadioGroup>
            </FormControl>
          </Card>
          <Card sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Delivery Date
            </Typography>
            <FormControl component="fieldset" required>
              <FormLabel component="legend">Select Delivery Date</FormLabel>
              <RadioGroup
                name="delivery_date"
                value={formData.delivery_date}
                onChange={handleChange}
              >
                {deliveryDateOptions.map((option) => (
                  <Box key={option.id}>
                    <FormControlLabel
                      value={option.name}
                      control={<Radio />}
                      label={option.name}
                    />
                    {option.name === "Immediate" &&
                      formData.delivery_date === option.name && (
                        <TextField
                          label="Hours"
                          name="delivery_date_hours"
                          type="number"
                          value={formData.delivery_date_hours}
                          onChange={handleChange}
                          inputProps={{ min: 1 }}
                          fullWidth
                          sx={{ mt: 1, ml: 4 }}
                          required
                        />
                      )}
                    {option.name === "Within" &&
                      formData.delivery_date === option.name && (
                        <TextField
                          label="Days"
                          name="delivery_date_days"
                          type="number"
                          value={formData.delivery_date_days}
                          onChange={handleChange}
                          inputProps={{ min: 1 }}
                          fullWidth
                          sx={{ mt: 1, ml: 4 }}
                          required
                        />
                      )}
                  </Box>
                ))}
              </RadioGroup>
            </FormControl>
          </Card>
          <Card sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Status
            </Typography>
            <FormControl fullWidth required>
              <Select
                name="status"
                value={formData.status}
                onChange={handleChange}
              >
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="cancelled">Cancelled</MenuItem>
                <MenuItem value="finalized">Finalized</MenuItem>
                <MenuItem value="expired">Expired</MenuItem>
              </Select>
            </FormControl>
          </Card>
        </Grid>
      </Grid>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          sx={{ width: "100%", whiteSpace: "pre-line" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default EditCustomerOrderForm;